/**
 * V105-3-6 — pure helpers for the infrastructure inventory review (InfraInventoryReview) and
 * the Scope tab callout (ComponentBoundary): one source for "what is left", the federal-data
 * chip and the bulk candidates, so the callout and the review never disagree.
 */
import { PROVIDERS, productDisplayName, type InfraItemReviewFacts } from '@aigovops/core';
import type { InventoryItem } from './infra-plan-api';

export type ReviewItem = InventoryItem & { review?: InfraItemReviewFacts | null; decided_by_name?: string | null };

/** High confidence: the bulk "Add N" takes these (and never an item that already matches a service). */
export const HIGH_CONFIDENCE = 0.85;
export const LOW_CONFIDENCE = 0.6;
export const confidenceLabel = (c: number) => (c >= HIGH_CONFIDENCE ? 'High' : c >= LOW_CONFIDENCE ? 'Medium' : 'Low');

/** The federal-data answer is incomplete: no Q1, or Q1 = no without its Q2 ("could it affect that data?"). */
export const federalPending = (i: ReviewItem) => !i.review?.federal.q1 || (i.review.federal.q1 === 'no' && !i.review.federal.q2);
/** Accepted / linked items whose federal-data answer is incomplete. */
export const needsFederalAnswer = (i: ReviewItem) => (i.status === 'accepted' || i.status === 'linked') && federalPending(i);
/** What it does: the person's, else the suggestion; null = nobody said (Add asks first). */
export const itemFunction = (i: Pick<ReviewItem, 'function' | 'suggested_function'>) => i.function ?? i.suggested_function ?? null;

export interface InventoryProgress {
  open: number;
  needsFederal: number;
  added: number;
  linked: number;
  dismissed: number;
  /** Nothing to review and every kept service has its federal-data answer. */
  complete: boolean;
}

export function inventoryProgress(items: readonly ReviewItem[]): InventoryProgress {
  const n = (s: string) => items.filter((i) => i.status === s).length;
  const open = n('open');
  const needsFederal = items.filter(needsFederalAnswer).length;
  return { open, needsFederal, added: n('accepted'), linked: n('linked'), dismissed: n('dismissed'), complete: items.length > 0 && open === 0 && needsFederal === 0 };
}

/** The Scope tab's line for the inventory callout (same counts as the review). */
export function inventoryCalloutText(p: InventoryProgress, total: number): { title: string; detail: string; action: string } {
  if (p.open) return { title: `${p.open} service${p.open === 1 ? '' : 's'} to review`, detail: 'found in code, connections and logs', action: `Review ${p.open}` };
  if (p.needsFederal) return { title: `${p.needsFederal} service${p.needsFederal === 1 ? '' : 's'} need the federal-data answer`, detail: 'decides each verdict', action: 'Answer them' };
  if (total) return { title: 'Inventory reviewed', detail: `${p.added} added, ${p.linked} linked, ${p.dismissed} dismissed`, action: 'Open the inventory' };
  return { title: 'Services you depend on', detail: 'none found yet', action: 'Find services' };
}

export type ChipTone = 'warn' | 'info' | 'ok' | undefined;
/** Federal-data chip: Not answered = warn, a suggestion waiting = info, answered = neutral. */
export function federalChip(i: ReviewItem): { label: string; tone: ChipTone } {
  const q1 = i.review?.federal.q1 ?? null;
  if (q1 === 'yes') return { label: 'Federal data: yes', tone: undefined };
  if (q1 === 'metadata_only') return { label: 'Federal data: metadata only', tone: undefined };
  if (q1 === 'no' && !i.review?.federal.q2) return { label: 'Federal data: one more question', tone: 'warn' };
  if (q1 === 'no') return { label: 'Federal data: no', tone: undefined };
  if (i.review?.federal.suggested) return { label: 'Federal data: suggestion to confirm', tone: 'info' };
  return { label: 'Federal data: not answered', tone: 'warn' };
}

/** Header bulk actions: high-confidence new items that say what they do (never a silent 'other'), low-confidence ones to dismiss. */
export function bulkCandidates<T extends ReviewItem>(open: readonly T[]): { high: T[]; low: T[] } {
  return {
    high: open.filter((i) => i.confidence >= HIGH_CONFIDENCE && !i.review?.existing_service && !!itemFunction(i)),
    low: open.filter((i) => i.confidence < LOW_CONFIDENCE && !i.review?.existing_service),
  };
}

/** Where focus goes after a row leaves the list: the next row, else the previous, else null (the heading). */
export function nextFocusId(ids: readonly string[], leaving: readonly string[]): string | null {
  const gone = new Set(leaving);
  const first = ids.findIndex((id) => gone.has(id));
  if (first < 0) return null;
  for (let k = first + 1; k < ids.length; k++) if (!gone.has(ids[k]!)) return ids[k]!;
  for (let k = first - 1; k >= 0; k--) if (!gone.has(ids[k]!)) return ids[k]!;
  return null;
}

/** Case-insensitive filter over names (no silent truncation; the caller shows the count). */
export function filterByName<T extends { name: string }>(list: readonly T[], q: string): T[] {
  const t = q.trim().toLowerCase();
  return t ? list.filter((x) => x.name.toLowerCase().includes(t)) : [...list];
}

/** Every product in the reviewed provider registry, as `{ ref, name }` (provider-level `*` when it lists none). */
export function productChoices(): Array<{ ref: string; name: string }> {
  const out: Array<{ ref: string; name: string }> = [];
  for (const p of PROVIDERS) {
    if (p.products?.length) for (const x of p.products) out.push({ ref: `${p.key}/${x.id}`, name: productDisplayName({ provider_key: p.key, product_id: x.id }) });
    else out.push({ ref: `${p.key}/*`, name: p.name });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** "2.4 MB" / "830 KB". */
export function fileSizeLabel(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
